import { isAxiosError } from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { addBreadcrumb } from "@sentry/react";
import { useAuth } from "@xemplo/auth-provider";
import { ErrorBoundary } from "@xemplo/error-boundary";
import { useMemberApi } from "@xemplo/gp-api";
import { memberActions } from "@xemplo/gp-member-store";
import { AuthorisationRoutes } from "@xemplo/gp-routes";
import { ExpedoUserProfile, LoginActions } from "@xemplo/gp-types";
import { XemploLoader } from "@xemplo/loader";
import { safeCallAsync } from "@xemplo/safe-call";
import { HeadingStandardSemiBold } from "@xemplo/style-constants";
import { XemploStateScopes } from "@xemplo/types";
import { useAppSettings } from "@xemplo/xemplo-app-settings";
import { usePatchXemploStateMutation } from "@xemplo/xemplo-state-query";

import { DEFAULT_HOME_ROUTE } from "../../constants";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { getReturnUrl } from "../../libs/return-url.helper";
import { ToastType, useToast } from "@xemplo/toast";

interface LoginProps {
  action: LoginActions;
}

/**
 * Cross-app nav persona query param. Formerly `GlobalNavQueryParam.Persona`
 * from @xemplo/types; that enum was removed in expedo-fe CORE-2588 (session
 * state now carries persona).
 * Keeping as a temp local const until proper refactor
 */
const PERSONA_QUERY_PARAM = "dsPersonaId";

const Message = styled.h1`
  ${HeadingStandardSemiBold};
  position: absolute;
  top: 40px;
  left: 50%;
  transform: translate(-50%, -50%);
`;

export const Login = ({ action }: LoginProps) => {
  const [message, setMessage] = useState("");
  const [getProfileInProgress, setGetProfileInProgress] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const { isLoading, isAuthenticated, signinRedirect, user } = useAuth();
  const { getProfile, cancelSignal, switchPersona } = useMemberApi();
  const { loadProfile, details } = useAppSelector((state) => state.member);
  const { addToast } = useToast();
  const { dsPersonaId, setDsPersonaId } = useAppSettings();
  const { mutateAsync: patchSessionState } = usePatchXemploStateMutation();
  const personaSyncAttemptedRef = useRef(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const needProfileData = useMemo(() => details == null, [details]);

  /**
   * There are different possible path after login.
   * 1. When profile data does not have the authorization DOM, it needs to sign the terms and conditions
   * 2. When profile data does not have a selected role, it needs to select a role
   * 3. It has a return url that is not within the login flow, it needs to redirect to that url
   * 4. Goes to the default home route
   */
  const handleNextRoute = async (
    profile: ExpedoUserProfile,
    ignoreSearchParams?: boolean
  ) => {
    /**
      If user has been redirected from another application with selected persona, update the selected persona
    **/
    const personaId = new URL(window.location.href).searchParams.get(
      PERSONA_QUERY_PARAM
    );
    if (personaId && !ignoreSearchParams) {
      handlePersonaUpdate(personaId, profile);
      return;
    }
    // If there is a dsPersonaId, it should be used as the current selected role or persona
    if (!profile.selectedRole && !dsPersonaId) {
      navigate("/select-role");
      return;
    }

    /**
     * Users can login directly from Pay and can have pre selected personas
     * In such scenarios, we need to update the session state with this persona id
     * Else the other apps visited from Pay won't be aware of this current selected persona
     */
    if (
      !dsPersonaId &&
      !personaSyncAttemptedRef.current &&
      profile.selectedRole?.directoryServicePersonaId
    ) {
      personaSyncAttemptedRef.current = true;
      const directoryPersonaId = profile.selectedRole.directoryServicePersonaId;
      setDsPersonaId(directoryPersonaId);
      // Best-effort: other apps just won't know about this persona yet if it
      // fails, so don't block navigation on it.
      safeCallAsync(
        patchSessionState({
          scope: XemploStateScopes.Session,
          data: { personaId: directoryPersonaId },
        })
      ).then(([, error]) => {
        if (error) {
          addBreadcrumb({
            category: "auth",
            message: "Failed to sync single/pre-selected persona to session state",
            level: "error",
            data: {
              personaId: directoryPersonaId,
              error: error instanceof Error ? error.message : String(error),
            },
          });
        }
      });
    }

    if (!profile.authorizationDOM) {
      navigate("/verifyDetails");
      return;
    }

    navigate(getReturnUrl() ?? DEFAULT_HOME_ROUTE);
  };

  const handlePersonaUpdate = async (
    personaId: string,
    profile: ExpedoUserProfile
  ) => {
    const selectedPersona = profile.userPersona?.find(
      (p) => p.directoryServicePersonaId === personaId
    );
    if (!selectedPersona) return;
    try {
      const profile = await switchPersona(selectedPersona);
      dispatch(memberActions.setDetails(profile));
      handleNextRoute(profile, true);
    } catch (error) {
      addToast({
        type: ToastType.Warning,
        text: error as any, // TODO: Not sure what the error object actually is
      });
    }
  };

  /**
   * Since we redirect to EIMS, we need to track the state of authentication
   * Triggering the getProfile directly here was having some issues as the result
   * of the getProfile modifies the member data and triggers the effect continuously
   * To fix this, created a separate effect to handle the getProfile call that depends
   * only on the member.loadprofile flag.
   */
  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated && action === LoginActions.Login) {
      setMessage("Checking credentials...");
      signinRedirect();
      return;
    }

    if (!isAuthenticated) return;
    if (!needProfileData) {
      handleNextRoute(details!);
      return;
    }
    setMessage("Getting user profile...");
    dispatch(memberActions.loadProfile());
  }, [
    signinRedirect,
    setMessage,
    isAuthenticated,
    isLoading,
    action,
    dispatch,
    needProfileData,
    handleNextRoute,
    dsPersonaId,
    patchSessionState,
  ]);

  /**
   * Needed to do this to avoid race condition issues
   * and to avoid the effect running multiple times when adding
   * dependency on the member data. Should probably look into using
   * Signals to avoid this convoluted solution.
   */
  useEffect(() => {
    if (errorCount > 2) return;
    if (loadProfile && needProfileData && !getProfileInProgress) {
      const cancelToken = cancelSignal.token;
      setGetProfileInProgress(true);
      getProfile(cancelToken)
        .then((profile) => {
          dispatch(memberActions.setDetails(profile));

          // TODO: There are code that still relies on the userArea coming from local storage
          // This should be removed once all the code is updated to use the member store

          localStorage.setItem("userArea", profile.area ?? "");
          handleNextRoute(profile);
        })
        .catch((error) => {
          const errorStatusCode = error.response?.status;
          if (
            isAxiosError(error) &&
            (errorStatusCode === 401 || errorStatusCode === 403)
          ) {
            navigate(`${AuthorisationRoutes.NoAccess}`);
          }
          setMessage(error.message);
          setErrorCount(errorCount + 1);
        })
        .finally(() => setGetProfileInProgress(false));
    }
  }, [
    loadProfile,
    needProfileData,
    cancelSignal,
    getProfile,
    dispatch,
    navigate,
    handleNextRoute,
    dsPersonaId,
    patchSessionState,
  ]);

  return (
    <ErrorBoundary>
      <Message>{message}</Message>
      {errorCount < 2 && <XemploLoader />}
    </ErrorBoundary>
  );
};

/** @alias */
export default Login;
